import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiCpu, FiLayers, FiHeart, FiTarget, FiZap, FiTrendingUp } from 'react-icons/fi';

const SolutionSection = () => {
  const rootSkills = [
    {
      title: "Attention Control",
      description: "Stay focused on tasks without getting lost or distracted"
    },
    {
      title: "Working Memory",
      description: "Hold spelling patterns, math steps, and instructions in mind while using them"
    },
    {
      title: "Processing Speed",
      description: "Think and respond quickly enough to keep up with learning demands"
    },
    {
      title: "Inhibitory Control",
      description: "Stop and think before responding, catch mistakes before making them"
    },
    {
      title: "Cognitive Flexibility",
      description: "Switch between ideas, strategies, and ways of thinking when stuck"
    }
  ];

  const leafSkills = [
    {
      title: "Phonological Awareness",
      description: "Break apart and blend sounds for reading and spelling mastery"
    },
    {
      title: "Visual-Spatial Processing",
      description: "Understand patterns, shapes, and spatial relationships for math and reading"
    },
    {
      title: "Sequential Processing",
      description: "Follow multi-step directions and organize information in logical order"
    },
    {
      title: "Pattern Recognition",
      description: "See the underlying structures in letters, words, and mathematical concepts"
    }
  ];

  const flowerSkills = [
    {
      title: "Reading Fluency",
      description: "Smooth, automatic reading that frees up brain power for understanding"
    },
    {
      title: "Written Expression",
      description: "Organize thoughts and get them onto paper clearly and efficiently"
    },
    {
      title: "Mathematical Reasoning",
      description: "Understand the \"why\" behind numbers and solve complex problems"
    },
    {
      title: "Study Skills",
      description: "Plan, organize, and execute learning independently"
    },
    {
      title: "Academic Confidence",
      description: "Tackle challenges with resilience"
    }
  ];

  const frameworks = [
    {
      icon: FiCpu,
      title: "For Dyslexia",
      description: "We target phonological processing and the EF skills that support reading comprehension, like working memory for following complex sentences and cognitive flexibility for making inferences",
      color: "accent-magenta"
    },
    {
      icon: FiZap,
      title: "For Dysgraphia",
      description: "We strengthen the motor planning, visual-spatial processing, and working memory needed to organize thoughts and get them onto paper",
      color: "accent-purple"
    },
    {
      icon: FiTrendingUp,
      title: "For NVLD & Math (Dyscalculia)",
      description: "We build the visual-spatial reasoning, problem-solving, and cognitive flexibility required to understand number concepts, patterns, and mathematical reasoning",
      color: "accent-gold"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-6">
            You Can't Keep Pouring Water into a Leaky Cup
          </h2>
          <div className="max-w-4xl mx-auto space-y-4 text-lg text-gray-700 leading-relaxed">
            <p>
              Traditional tutoring focuses on the <em>what</em> (the curriculum). It tries to pour more water in. But if the cup (the brain's processing system) has leaks (EF deficits), progress will always be slow and frustrating.
            </p>
            <p className="font-semibold text-gray-900">
              Executive Function Cognitive Therapy is different. It patches the leaks and strengthens the cup. We target the core cognitive skills that are the foundation for all learning:
            </p>
          </div>
        </motion.div>

        {/* Core Skills Overview */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {[
            { title: "Working Memory", description: "Holding and manipulating information (e.g., sounding out a word while remembering what came before)." },
            { title: "Cognitive Flexibility", description: "Shifting approaches when solving a problem (e.g., trying a new math strategy)." },
            { title: "Inhibitory Control", description: "Ignoring distractions (e.g., tuning out noise to focus on reading)." },
            { title: "Processing Speed", description: "Taking in and responding to information quickly and efficiently." }
          ].map((skill, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card text-center"
            >
              <div className="w-12 h-12 bg-accent-magenta/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiCpu} className="h-6 w-6 text-accent-magenta" />
              </div>
              <h3 className="font-bold text-gray-900 mb-3">{skill.title}</h3>
              <p className="text-gray-600 text-sm">{skill.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Framework for Learning Disorders */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <h3 className="text-2xl lg:text-3xl font-playfair font-bold text-gray-900 mb-8 text-center">
            Our EF Therapy Framework for Learning Disorders:
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {frameworks.map((framework, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="card"
              >
                <div className={`w-16 h-16 bg-${framework.color}/10 rounded-full flex items-center justify-center mb-6`}>
                  <SafeIcon icon={framework.icon} className={`h-8 w-8 text-${framework.color}`} />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-4">{framework.title}</h4>
                <p className="text-gray-600 leading-relaxed">{framework.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Comprehensive Brain-Building System */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-playfair font-bold text-gray-900 mb-4">
              Our Comprehensive Brain-Building System:
            </h3>
            <p className="text-lg text-gray-600">(Based on the Sumpter Model of EF)</p>
          </div>

          <div className="space-y-16">
            {/* Root Skills */}
            <div>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="flex items-center gap-4 mb-8"
              >
                <div className="w-16 h-16 bg-accent-magenta/10 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiCpu} className="h-8 w-8 text-accent-magenta" />
                </div>
                <h4 className="text-2xl font-bold text-gray-900">🧠 Root Skills (Cognitive Foundation)</h4>
              </motion.div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rootSkills.map((skill, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-accent-magenta/5 p-6 rounded-xl border border-accent-magenta/20"
                  >
                    <h5 className="font-bold text-gray-900 mb-2">{skill.title}:</h5>
                    <p className="text-gray-600 text-sm">{skill.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Leaf Skills */}
            <div>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="flex items-center gap-4 mb-8"
              >
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiLayers} className="h-8 w-8 text-green-600" />
                </div>
                <h4 className="text-2xl font-bold text-gray-900">🌱 Leaf Skills (Learning Connections)</h4>
              </motion.div>
              <div className="grid md:grid-cols-2 gap-6">
                {leafSkills.map((skill, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-green-50 p-6 rounded-xl border border-green-200"
                  >
                    <h5 className="font-bold text-gray-900 mb-2">{skill.title}:</h5>
                    <p className="text-gray-600 text-sm">{skill.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Flower Skills */}
            <div>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="flex items-center gap-4 mb-8"
              >
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiHeart} className="h-8 w-8 text-pink-600" />
                </div>
                <h4 className="text-2xl font-bold text-gray-900">🌸 Flower Skills (Academic Mastery)</h4>
              </motion.div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {flowerSkills.map((skill, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-pink-50 p-6 rounded-xl border border-pink-200"
                  >
                    <h5 className="font-bold text-gray-900 mb-2">{skill.title}:</h5>
                    <p className="text-gray-600 text-sm">{skill.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SolutionSection;